import { useState, useEffect } from 'react';
import api from '../lib/api';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { X, Megaphone, Sparkles, Wrench, Calendar, Zap, BarChart3 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CATEGORY_CONFIG = {
  update: { icon: Sparkles, label: 'Update', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  feature: { icon: Zap, label: 'New Feature', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  maintenance: { icon: Wrench, label: 'Maintenance', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' },
  event: { icon: Calendar, label: 'Event', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  balance_change: { icon: BarChart3, label: 'Balance Change', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
};

/**
 * Developer News Screen — shown after splash, before sign-in.
 * Displays latest announcements from the game developers.
 * Also reusable as an embedded view inside the News tab.
 */
export default function DeveloperNewsScreen({ onClose, embedded = false }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/developer-news', { params: { limit: 20 } }).then(res => {
      setNews(res.data.news || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // UX-1: in overlay mode, allow dismissing with the Escape key so the
  // announcement doesn't feel like a forced gate. Embedded mode (News tab)
  // has no onClose and shouldn't bind a global key handler.
  useEffect(() => {
    if (embedded || !onClose) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [embedded, onClose]);

  const formatDate = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const content = (
    <div className={embedded ? '' : 'p-4'}>
      {!embedded && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>Developer News</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="dev-news-close">
            <span className="hidden sm:inline text-[10px] text-muted-foreground mr-1.5">Esc</span>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full rounded-2xl" />)}</div>
      ) : news.length === 0 ? (
        <div className="text-center py-12">
          <Megaphone className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground text-sm">No announcements yet. Stay tuned!</p>
        </div>
      ) : (
        <ScrollArea className={embedded ? 'h-[calc(100vh-280px)]' : 'h-[calc(100vh-120px)]'}>
          <div className="space-y-3">
            {news.map((item, i) => {
              const config = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.update;
              const Icon = config.icon;
              return (
                <div key={item.id || i} className="bg-card border border-border/60 rounded-2xl p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-[9px] ${config.color}`}>
                          <Icon className="h-2.5 w-2.5 mr-0.5" /> {config.label}
                        </Badge>
                        {item.requires_app_update && (
                          <Badge variant="destructive" className="text-[9px]">App Update</Badge>
                        )}
                        {item.version && (
                          <Badge variant="secondary" className="text-[9px]">v{item.version}</Badge>
                        )}
                      </div>
                      <h3 className="text-sm font-semibold">{item.title}</h3>
                    </div>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {formatDate(item.published_at || item.created_at)}
                    </span>
                  </div>
                  <p className="text-xs text-foreground/80 leading-relaxed whitespace-pre-line">
                    {item.content}
                  </p>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </div>
  );

  if (embedded) return content;

  // Full-screen overlay mode (shown after splash)
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] bg-background"
        data-testid="developer-news-screen"
      >
        {content}
      </motion.div>
    </AnimatePresence>
  );
}
